import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db, auth } from "../../firebase/firebase.js";
import { useAuth } from "../../contexts/authContext/index.jsx";
import { logError } from "../../firebase/errorReporting";

/**
 * Gate for the develop build at develop.goldfinder.net.au.
 *
 * Production is already behind login and SubscriptionGaurd, so this is not
 * protecting the data - it is keeping paying customers off an unfinished build
 * they did not ask to be part of. It compiles in only when REACT_APP_BETA is
 * set, so the production bundle is byte-for-byte unaffected.
 *
 * Enrolment is a `betaTester: true` field on the user's own `user_info`
 * document, which is togglable from the Firebase console in a few seconds -
 * deliberately not a custom claim like `admin`, because setting one of those
 * needs the Admin SDK and a service account, and adding a tester should not
 * need a script. Admins pass automatically; they already carry a claim that
 * cannot be forged client-side.
 *
 * A tester who is not enrolled is told where to go rather than bounced to a
 * blank page - they are almost always a real customer who followed a link.
 */
export const BETA_BUILD = process.env.REACT_APP_BETA === 'true';

const styles = {
  wrap: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24, fontFamily: 'var(--gf-font-sans, "Poppins", sans-serif)',
    background: 'var(--gf-secondary-50, #F5F6F7)',
  },
  card: {
    maxWidth: 460, background: '#fff', borderRadius: 14, padding: '28px 30px',
    boxShadow: '0 8px 30px rgba(0,0,0,0.12)', textAlign: 'center',
  },
  h: { fontSize: 20, fontWeight: 600, margin: '0 0 10px', color: 'var(--gf-secondary-800, #1F2327)' },
  p: { fontSize: 14, lineHeight: 1.6, color: 'var(--gf-secondary-500, #4B5563)', margin: '0 0 20px' },
  a: {
    display: 'inline-block', padding: '10px 18px', borderRadius: 9, fontWeight: 600,
    fontSize: 14, background: 'var(--gf-primary-400, #FD7218)', color: '#fff', textDecoration: 'none',
  },
};

export const BetaGuard = ({ component: C }) => {
  // Reactivity comes from the context: it renders `{!loading && children}` and
  // re-renders when auth resolves, whereas the Firebase singleton changes
  // underneath React without telling it - reading that alone left signed-out
  // visitors stuck on the 'checking' placeholder, a blank page.
  //
  // But the token has to come from auth.currentUser. The context stores
  // `{ ...user }` - a plain spread of the Firebase User - and User's methods
  // live on the prototype, so getIdTokenResult is NOT on the copy. Calling it
  // threw a TypeError straight into the catch below, which fails closed, so
  // admins were being told they are not in the beta. AdminGaurd reads
  // auth.currentUser for exactly this reason.
  const { currentUser, userLoggedIn } = useAuth();

  // 'checking' until we know, so a slow Firestore read never flashes the
  // rejection screen at someone who is actually enrolled.
  const [state, setState] = useState('checking');

  useEffect(() => {
    if (!BETA_BUILD) { setState('allowed'); return undefined; }

    // Not signed in: nothing to check yet. Hand straight through so the guard
    // underneath can do what it does in production - send them to log in.
    // Gating an anonymous visitor here would only replace the login screen
    // with a "you're not in the beta" screen they cannot act on.
    if (!userLoggedIn || !currentUser) { setState('allowed'); return undefined; }

    let cancelled = false;
    (async () => {
      try {
        const token = await auth.currentUser.getIdTokenResult();
        if (token.claims.admin || token.claims.beta) {
          if (!cancelled) setState('allowed');
          return;
        }
        const snap = await getDoc(doc(db, "user_info", auth.currentUser.uid));
        if (cancelled) return;
        setState(snap.exists() && snap.data()?.betaTester === true ? 'allowed' : 'denied');
      } catch (err) {
        logError(err, { component: 'BetaGuard' });
        // Fail closed: an unreadable enrolment is not an enrolment.
        if (!cancelled) setState('denied');
      }
    })();

    return () => { cancelled = true; };
  }, [userLoggedIn, currentUser?.uid]);

  if (state === 'checking') return <div className="page-layout" />;
  if (state === 'denied') {
    return (
      <div style={styles.wrap}>
        <div style={styles.card}>
          <h1 style={styles.h}>This is the GoldFinder test site</h1>
          <p style={styles.p}>
            You're signed in, but your account isn't part of the current test
            group. Everything here is unreleased and may change or break.
            The live site is where you want to be.
          </p>
          <a style={styles.a} href="https://map.goldfinder.net.au">Go to GoldFinder</a>
        </div>
      </div>
    );
  }
  return <C />;
};

export default BetaGuard;
